/**
 * Custom Image Upload Component
 * Handles image upload with cropping functionality using Cropper.js
 */

class CustomImageUpload extends HTMLElement {
  constructor() {
    super();
    this.cropper = null;
    this.croppedImageData = null;
    this.originalFile = null;
    this.isModalOpen = false;
    
    // Check localStorage immediately to prevent flicker
    // This runs synchronously before any rendering
    this._checkSavedImageSync();
  }
  
  // Synchronous check for saved image - prevents flicker on variant change
  _checkSavedImageSync() {
    try {
      const storageKey = this.dataset.storageKey || 
        `custom_image_${this.dataset.productHandle}_${this.dataset.blockId}`;
      if (localStorage.getItem(storageKey)) {
        this.setAttribute('data-has-saved-image', 'true');
      }
    } catch (e) {
      // localStorage not available
    }
  }

  connectedCallback() {
    this.config = {
      maxSize: parseInt(this.dataset.maxSize) || 10, // MB
      cropRatio: this.dataset.cropRatio || '1',
      autoAddCart: this.dataset.autoAddCart === 'true',
      required: this.dataset.required === 'true',
      blockId: this.dataset.blockId || '',
      productHandle: this.dataset.productHandle || '',
      isCircle: this.dataset.isCircle === 'true',
      fieldName: this.dataset.fieldName || 'Custom Photo',
    };

    this.cacheElements();
    this.bindEvents();
    this.loadSavedImage();
  }

  cacheElements() {
    this.fileInput = this.querySelector('[data-image-input]');
    this.uploadButtons = this.querySelectorAll('[data-upload-button]');
    this.previewContainer = this.querySelector('[data-preview-container]');
    this.previewImage = this.querySelector('[data-preview-image]');
    this.removeButton = this.querySelector('[data-remove-button]');
    this.hiddenInput = this.querySelector('[data-hidden-input]');
    this.modal = this.querySelector('[data-crop-modal]');
    this.modalImage = this.querySelector('[data-crop-image]');
    this.confirmButton = this.querySelector('[data-confirm-button]');
    this.cancelButton = this.querySelector('[data-cancel-button]');
    this.closeButton = this.querySelector('[data-close-button]');
    this.rotateLeftBtn = this.querySelector('[data-rotate-left]');
    this.rotateRightBtn = this.querySelector('[data-rotate-right]');
    this.zoomInBtn = this.querySelector('[data-zoom-in]');
    this.zoomOutBtn = this.querySelector('[data-zoom-out]');
  }

  bindEvents() {
    // File input change
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (e) => {
        e.stopPropagation();
        this.handleFileSelect(e);
      });
    }
    
    // Upload button click - bind to all upload buttons
    this.uploadButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.fileInput) {
          this.fileInput.click();
        }
      });
    });
    
    // Remove button
    if (this.removeButton) {
      this.removeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeImage();
      });
    }
    
    // Modal controls - prevent event propagation
    if (this.confirmButton) {
      this.confirmButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.confirmCrop();
      });
    }
    
    if (this.cancelButton) {
      this.cancelButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
    
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
    
    // Rotation and zoom - prevent propagation
    if (this.rotateLeftBtn) {
      this.rotateLeftBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.cropper) this.cropper.rotate(-90);
      });
    }
    
    if (this.rotateRightBtn) {
      this.rotateRightBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.cropper) this.cropper.rotate(90);
      });
    }
    
    if (this.zoomInBtn) {
      this.zoomInBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.cropper) this.cropper.zoom(0.1);
      });
    }
    
    if (this.zoomOutBtn) {
      this.zoomOutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.cropper) this.cropper.zoom(-0.1);
      });
    }
    
    // Close modal on overlay click
    const overlay = this.modal?.querySelector('[data-modal-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal();
      });
    }
    
    // Prevent clicks inside modal content from closing
    const modalContent = this.modal?.querySelector('.custom-image-upload__modal-content');
    if (modalContent) {
      modalContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen) {
        this.closeModal();
      }
    });
    
    // Listen for variant changes to ensure image persists
    // Use document-level listener since the element may be replaced during morph
    this._variantMorphHandler = (e) => {
      // Check if this event is relevant to our product
      const productHandle = this.config.productHandle;
      
      // Small delay to ensure DOM is fully updated, then find and restore
      setTimeout(() => {
        // Find the custom-image-upload element (may be a new instance after morph)
        const uploadElement = document.querySelector(`custom-image-upload[data-product-handle="${productHandle}"]`);
        if (uploadElement && uploadElement !== this) {
          // New element was created, it will load from localStorage in connectedCallback
        } else if (uploadElement === this) {
          // Same element, just restore
          this.restoreImageAfterMorph();
        }
      }, 100);
    };
    
    document.addEventListener('variant:morphed', this._variantMorphHandler);
    
    // Also listen on document for broader compatibility
    document.addEventListener('theme:variant:update', () => this.ensureImagePersists());
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    if (this._variantMorphHandler) {
      document.removeEventListener('variant:morphed', this._variantMorphHandler);
    }
  }
  
  // Restore image after DOM morph (variant change)
  restoreImageAfterMorph() {
    // Re-cache elements as they may have been replaced
    this.cacheElements();
    
    // Restore from localStorage
    this.loadSavedImage();
  }
  
  // Ensure the image stays when variant changes
  ensureImagePersists() {
    if (this.croppedImageData && this.hiddenInput) {
      // Re-populate the file input if it was cleared
      if (!this.hiddenInput.files || this.hiddenInput.files.length === 0) {
        fetch(this.croppedImageData)
          .then(res => res.blob())
          .then(blob => {
            const fileName = 'cropped-restored-image.jpg';
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            this.hiddenInput.files = dataTransfer.files;
          })
          .catch(err => console.warn('[CustomImageUpload] Failed to re-populate file input:', err));
      }
    }
  }

  handleFileSelect(event) {
    const file = event.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF, etc.)');
      this.resetFileInput();
      return;
    }

    // Validate file size
    const maxSizeBytes = this.config.maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      alert(`File size must be less than ${this.config.maxSize}MB`);
      this.resetFileInput();
      return;
    }

    this.originalFile = file;
    this.openCropModal(file);
  }

  resetFileInput() {
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  openCropModal(file) {
    // Check if Cropper is available
    if (typeof Cropper === 'undefined') {
      console.error('[CustomImageUpload] Cropper.js is not loaded');
      alert('Image cropper is loading, please try again in a moment.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (!result || !this.modalImage) return;
      
      // Set image source
      this.modalImage.src = result;
      
      // Show modal
      if (this.modal) {
        this.modal.classList.add('is-open');
        this.isModalOpen = true;
        document.body.style.overflow = 'hidden';
      }
      
      // Initialize cropper after image loads
      if (this.modalImage.complete) {
        // Image already loaded (cached)
        setTimeout(() => this.initCropper(), 100);
      } else {
        this.modalImage.onload = () => {
          setTimeout(() => this.initCropper(), 100);
        };
      }
    };
    
    reader.onerror = () => {
      console.error('[CustomImageUpload] Failed to read file');
      alert('Failed to read the image file. Please try again.');
    };
    
    reader.readAsDataURL(file);
  }

  initCropper() {
    if (!this.modalImage || !this.isModalOpen) return;
    
    // Destroy existing cropper
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    // Check if Cropper is available
    if (typeof Cropper === 'undefined') {
      console.error('[CustomImageUpload] Cropper.js is not loaded');
      return;
    }

    // Determine aspect ratio
    let aspectRatio;
    if (this.config.cropRatio === 'free') {
      aspectRatio = NaN; // Free crop
    } else {
      aspectRatio = parseFloat(this.config.cropRatio);
    }

    try {
      // Initialize Cropper.js
      this.cropper = new Cropper(this.modalImage, {
        aspectRatio: aspectRatio,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 0.9,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minContainerWidth: 200,
        minContainerHeight: 200,
        ready: () => {
          console.log('[CustomImageUpload] Cropper initialized successfully');
        }
      });
    } catch (error) {
      console.error('[CustomImageUpload] Failed to initialize cropper:', error);
    }
  }

  confirmCrop() {
    if (!this.cropper) return;

    // Get cropped canvas - this returns ONLY the cropped area, not the full image
    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: 1200,
      maxHeight: 1200,
      fillColor: '#fff',
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    if (!canvas) return;

    // Convert to blob and set to file input
    canvas.toBlob((blob) => {
      if (!blob) return;
      
      // Create a file from the blob
      const fileName = this.originalFile ? `cropped-${this.originalFile.name}` : 'cropped-image.jpg';
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      
      // Use DataTransfer to set the file input value
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      
      if (this.hiddenInput) {
        this.hiddenInput.files = dataTransfer.files;
      }
      
      // Update preview
      this.croppedImageData = canvas.toDataURL('image/jpeg', 0.9);
      if (this.previewImage) {
        this.previewImage.src = this.croppedImageData;
      }
      
      // Show preview, hide upload buttons
      this.previewContainer?.classList.add('has-image');
      this.uploadButtons.forEach(btn => btn.classList.add('hidden'));
      
      // Save to localStorage for persistence (still useful for UI state)
      this.saveImageToStorage();
      
      // Close modal
      this.closeModal();
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('image:uploaded', {
        bubbles: true,
        detail: { imageData: this.croppedImageData, file: file }
      }));
      
      // Auto add to cart if enabled
      if (this.config.autoAddCart) {
        this.triggerAddToCart();
      }
    }, 'image/jpeg', 0.9);
  }

  closeModal() {
    this.isModalOpen = false;
    this.modal?.classList.remove('is-open');
    document.body.style.overflow = '';
    
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    
    // Reset file input
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  removeImage() {
    this.croppedImageData = null;
    this.originalFile = null;
    
    if (this.previewImage) {
      this.previewImage.src = '';
    }
    
    // Clear hidden input files properly using DataTransfer
    if (this.hiddenInput) {
      try {
        const emptyDataTransfer = new DataTransfer();
        this.hiddenInput.files = emptyDataTransfer.files;
      } catch (e) {
        // Fallback for older browsers
        this.hiddenInput.value = '';
      }
    }
    
    // Clear the file input as well
    this.resetFileInput();
    
    this.previewContainer?.classList.remove('has-image');
    this.uploadButtons.forEach(btn => btn.classList.remove('hidden'));
    
    // Remove the data-has-saved-image attribute
    this.removeAttribute('data-has-saved-image');
    
    // Remove from localStorage
    this.removeImageFromStorage();
    
    // Dispatch event
    this.dispatchEvent(new CustomEvent('image:removed', { bubbles: true }));
    
    console.log('[CustomImageUpload] Image removed successfully');
  }

  getStorageKey() {
    return `custom_image_${this.config.productHandle}_${this.config.blockId}`;
  }

  saveImageToStorage() {
    try {
      const key = this.getStorageKey();
      // Store a compressed version or just a flag
      // Full base64 might be too large for localStorage
      localStorage.setItem(key, this.croppedImageData);
    } catch (e) {
      console.warn('[CustomImageUpload] Could not save to localStorage:', e);
    }
  }

  loadSavedImage() {
    try {
      const key = this.getStorageKey();
      const savedImage = localStorage.getItem(key);
      
      if (savedImage && savedImage.startsWith('data:image')) {
        this.croppedImageData = savedImage;
        
        if (this.previewImage) {
          this.previewImage.src = savedImage;
        }
        
        // Reconstruct File object from base64 for the file input
        if (this.hiddenInput) {
          // Convert base64 to blob
          fetch(savedImage)
            .then(res => res.blob())
            .then(blob => {
              // IMPORTANT: File name must start with 'cropped-' to be recognized as cropped version
              const fileName = 'cropped-restored-image.jpg';
              const file = new File([blob], fileName, { type: 'image/jpeg' });
              
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(file);
              this.hiddenInput.files = dataTransfer.files;
            })
            .catch(err => console.warn('[CustomImageUpload] Failed to restore file input:', err));
        }
        
        this.previewContainer?.classList.add('has-image');
        this.uploadButtons.forEach(btn => btn.classList.add('hidden'));
      }
    } catch (e) {
      console.warn('[CustomImageUpload] Could not load from localStorage:', e);
    }
  }

  removeImageFromStorage() {
    try {
      const key = this.getStorageKey();
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('[CustomImageUpload] Could not remove from localStorage:', e);
    }
  }

  triggerAddToCart() {
    // Find the add to cart form and submit it
    const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      // Small delay to ensure the hidden input is updated
      setTimeout(() => {
        const submitButton = form.querySelector('[type="submit"], [name="add"]');
        if (submitButton) {
          submitButton.click();
        }
      }, 100);
    }
  }

  // Public method to get the cropped image data
  getImageData() {
    return this.croppedImageData;
  }

  // Public method to check if image is uploaded
  hasImage() {
    return !!this.croppedImageData;
  }
}

// Define custom element
if (!customElements.get('custom-image-upload')) {
  customElements.define('custom-image-upload', CustomImageUpload);
}
